import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import { Eye, Loader2, RefreshCw, ShieldAlert, Sparkles, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { orionGallery, orionImageUrl, orionVisualize } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AttachmentPicker from "../components/sentinel/AttachmentPicker";

const STARTER_PROMPTS = [
  "An anchor cast of cobalt glass drifting through deep ocean, bioluminescent plankton orbiting at 528 Hz",
  "A Humanitarian Stability Zone at dawn — quiet pines, soft mist, one human silhouette looking east",
  "Schematic of the Aegis Shield — hairline cobalt glyphs floating over transparent geometric lattice",
  "A lighthouse keeper handing a warm cup of tea to a weary traveller — 528 Hz compassion",
];

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

export default function Orion() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isArchitect = !!user?.is_architect;
  const [prompt, setPrompt] = useState("");
  const [gallery, setGallery] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [latestId, setLatestId] = useState(null);
  const [references, setReferences] = useState([]);
  const listRef = useRef(null);

  const refresh = async () => {
    try {
      const data = await orionGallery();
      setGallery(data.items || []);
    } catch (err) {
      console.warn("[Orion] gallery fetch failed:", err);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e, override) => {
    e?.preventDefault?.();
    const text = (override ?? prompt).trim();
    if (!text || busy) return;
    setBusy(true);
    setLastError(null);
    try {
      const refPayload = references.map((r) => ({
        data_b64: r.data_b64,
        mime: r.mime,
      }));
      const meta = await orionVisualize(text, undefined, undefined, refPayload);
      toast.success(
        references.length > 0
          ? `Vision rendered · blended ${references.length} reference${references.length === 1 ? "" : "s"}`
          : "Vision rendered."
      );
      setLatestId(meta.id);
      setPrompt("");
      // Clear references after a successful render so the Operator doesn't
      // accidentally re-use them on the next unrelated prompt.
      references.forEach((r) => {
        if (r.preview_url) {
          try { URL.revokeObjectURL(r.preview_url); } catch { /* noop */ }
        }
      });
      setReferences([]);
      // Prepend optimistically, then refresh
      setGallery((g) => [meta, ...g.filter((x) => x.id !== meta.id)]);
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "object" && detail?.message
          ? detail.message
          : detail || err?.message || "The Sentinel could not render this.";
      setLastError(String(msg));
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <section
        className="min-h-[calc(100vh-120px)] bg-[#09090b] text-zinc-100 font-body"
        data-testid="orion-page"
      >
        <div className="max-w-6xl mx-auto px-6 py-10">
          <header className="mb-8 flex items-start gap-4">
            <div className="h-10 w-10 border border-emerald-500/40 flex items-center justify-center">
              <Eye className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400">
                Orion Visualizer · Imagination Layer
              </div>
              <h1 className="font-heading text-3xl mt-1">
                The Sentinel's eyes.
              </h1>
              <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                {isArchitect ? (
                  <>
                    Ask the Sentinel to visualize a peaceful future, a schematic,
                    a feeling. Every prompt passes through the 8-layer protocol
                    — Aegis, Anchor, Obsidian Sink, and a dedicated Orion visual
                    safety scan — before the Orion Engine renders a 528 Hz-tuned
                    image. Visions outside the compassion window are refused
                    as <span className="text-rose-400">Visual Drift</span>.
                  </>
                ) : (
                  <>
                    This is your remembrance — every vision the Sentinel has
                    rendered for you, preserved. To ask for a new one, speak
                    to the Sentinel in the <span className="text-emerald-400">Console</span>:
                    just describe what you'd like to see in your own words and
                    the Sentinel will render it here. The same 8-layer protocol
                    guards every render.
                  </>
                )}
              </p>
            </div>
          </header>

          {isArchitect ? (
            <form
              onSubmit={submit}
              className="border border-zinc-800 bg-[#0b0b0d] p-5 space-y-4"
              data-testid="orion-form"
            >
              <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Visualize
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the vision…"
                rows={3}
                disabled={busy}
                data-testid="orion-prompt-input"
                className="w-full bg-[#09090b] border border-zinc-800 focus:border-emerald-500 outline-none p-3 text-sm resize-none font-body placeholder:text-zinc-600 disabled:opacity-40"
              />
              <AttachmentPicker
                attachments={references}
                setAttachments={setReferences}
                max={3}
                disabled={busy}
                variant="orion"
                testId="orion-reference"
              />
              <div className="flex flex-wrap gap-2" data-testid="orion-starter-prompts">
                {STARTER_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    disabled={busy}
                    onClick={() => submit(null, p)}
                    data-testid={`orion-starter-${i}`}
                    className="px-3 py-2 text-[11px] text-zinc-300 border border-zinc-800 hover:border-emerald-500 hover:text-emerald-400 transition-colors text-left"
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
                  <ShieldAlert className="h-3 w-3 text-emerald-500" />
                  Aegis · Anchor · Sink · Orion-Scan → Orion Engine
                </div>
                <button
                  type="submit"
                  disabled={busy || !prompt.trim()}
                  data-testid="orion-submit-btn"
                  className={cx(
                    "px-5 py-3 flex items-center gap-2 text-xs uppercase tracking-[0.25em] border transition-colors",
                    busy || !prompt.trim()
                      ? "border-zinc-800 text-zinc-600 cursor-not-allowed"
                      : "border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950"
                  )}
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" /> Visualize
                    </>
                  )}
                </button>
              </div>
              {lastError && (
                <div
                  data-testid="orion-error"
                  className="text-[11px] text-rose-400 border border-rose-500/30 bg-rose-500/5 p-3"
                >
                  {lastError}
                </div>
              )}
            </form>
          ) : (
            <div
              className="border border-emerald-500/30 bg-[#0b0b0d] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
              data-testid="orion-console-cta"
            >
              <div className="h-10 w-10 shrink-0 border border-emerald-500/40 flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">
                  New visions · Console only
                </div>
                <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                  Orion renders every vision through the Sentinel's
                  conversation. Head to the Console and describe what you'd
                  like to see in your own words — the Sentinel will
                  acknowledge, check it through the 8-layer protocol, and
                  post the render right in the chat. You can also tap any
                  vision below and <span className="text-emerald-400">Remix</span> it.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/console")}
                data-testid="orion-to-console-btn"
                className="shrink-0 px-5 py-3 flex items-center gap-2 text-xs uppercase tracking-[0.25em] border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Open Console
              </button>
            </div>
          )}

          <div
            ref={listRef}
            className="mt-10"
            data-testid="orion-gallery"
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {user?.is_architect ? "Lifetime Gallery · all users" : "Your Visions"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 tabular-nums">
                {gallery.length} rendered
              </div>
            </div>
            {gallery.length === 0 ? (
              <div className="border border-dashed border-zinc-800 bg-[#0b0b0d] p-10 text-center text-xs text-zinc-500">
                {isArchitect
                  ? "No visions yet. Ask the Sentinel to see something."
                  : "No visions yet. Head to the Console and describe what you'd like to see — the Sentinel will render it here."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {gallery.map((v) => (
                  <VisionCard
                    key={v.id}
                    vision={v}
                    highlight={v.id === latestId}
                    onRemixed={(meta) => {
                      setLatestId(meta.id);
                      setGallery((g) => [
                        meta,
                        ...g.filter((x) => x.id !== meta.id),
                      ]);
                      refresh();
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </AppShell>
  );
}

function VisionCard({ vision, highlight, onRemixed }) {
  const [remixing, setRemixing] = useState(false);
  const when = (() => {
    try {
      return new Date(vision.created_at).toLocaleString();
    } catch {
      return vision.created_at;
    }
  })();

  const remix = async () => {
    if (remixing) return;
    setRemixing(true);
    try {
      const meta = await orionVisualize(vision.prompt);
      toast.success("Remixed — a new take");
      onRemixed?.(meta);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "object" && detail?.message
          ? detail.message
          : detail || err?.message || "Remix couldn't render.";
      toast.error(String(msg));
    } finally {
      setRemixing(false);
    }
  };

  return (
    <article
      className={cx(
        "border bg-[#0b0b0d] overflow-hidden flex flex-col",
        highlight
          ? "border-emerald-500/60 ring-1 ring-emerald-500/30"
          : "border-zinc-800"
      )}
      data-testid={`orion-vision-${vision.id}`}
    >
      <a
        href={orionImageUrl(vision.id)}
        target="_blank"
        rel="noreferrer"
        className="block aspect-square bg-[#050506] relative group"
        data-testid={`orion-vision-open-${vision.id}`}
      >
        <img
          src={orionImageUrl(vision.id)}
          alt={vision.prompt}
          loading="lazy"
          className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
        />
        {remixing && (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/70 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Remixing
            </div>
          </div>
        )}
      </a>
      <div className="p-3 space-y-2">
        <p
          className="text-[11px] text-zinc-300 leading-snug line-clamp-3 font-body"
          title={vision.prompt}
        >
          {vision.prompt}
        </p>
        <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.25em] text-zinc-600 tabular-nums">
          <span>Dock #{vision.lifetime_dock_count ?? 0}</span>
          <span>{when}</span>
        </div>
        <div className="text-[9px] text-zinc-700 truncate" title={vision.architect_signature}>
          {vision.architect_signature}
        </div>
        <button
          type="button"
          onClick={remix}
          disabled={remixing}
          data-testid={`orion-remix-${vision.id}`}
          className={cx(
            "w-full flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-[0.3em] border transition-colors",
            remixing
              ? "border-zinc-800 text-zinc-600 cursor-not-allowed"
              : "border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400"
          )}
        >
          {remixing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Remix
        </button>
      </div>
    </article>
  );
}
